/**
 * Generated by Kubb (https://kubb.dev/).
 * Do not edit manually.
 */

import type { RequestConfig, ResponseErrorConfig } from '../.kubb/client'
import type {
  GetAssetYieldDataOptions,
  GetAssetYieldDataStatus200,
  GetAssetYieldDataStatus400,
  GetAssetYieldDataStatus404,
  GetAssetYieldDataStatus500,
  GetAssetYieldDataStatus504,
} from '../types/GetAssetYieldData'
import type {
  QueryKey,
  QueryClient,
  QueryObserverOptions,
  UseQueryResult,
} from '@tanstack/react-query'
import { getAssetYieldData } from '../clients/getAssetYieldData'
import { queryOptions, useQuery } from '@tanstack/react-query'

export const getAssetYieldDataQueryKey = ({
  path,
  query,
}: Omit<GetAssetYieldDataOptions, 'headers'>) =>
  [{ url: '/v1/charts/:asset_id/yield', params: path }, ...(query ? [query] : [])] as const

type GetAssetYieldDataQueryKey = ReturnType<typeof getAssetYieldDataQueryKey>

export function getAssetYieldDataQueryOptions(
  { path, query }: GetAssetYieldDataOptions,
  config: Partial<Omit<RequestConfig, 'path' | 'query' | 'body' | 'headers' | 'url'>> = {},
) {
  const queryKey = getAssetYieldDataQueryKey({ path, query })
  return queryOptions<
    GetAssetYieldDataStatus200,
    ResponseErrorConfig<
      | GetAssetYieldDataStatus400
      | GetAssetYieldDataStatus404
      | GetAssetYieldDataStatus500
      | GetAssetYieldDataStatus504
    >,
    GetAssetYieldDataStatus200,
    typeof queryKey
  >({
    queryKey,
    queryFn: async ({ signal }) => {
      return getAssetYieldData({
        ...config,
        path,
        query,
        signal: config.signal ?? signal,
        throwOnError: true,
      }).unwrap()
    },
  })
}

/**
 * @description Returns complete yield buckets starting at `start`; `end` is exclusive and a trailing partial bucket is omitted. Requests are limited to 10,000 complete buckets. Public callers may query only the last month. Authenticated callers may query up to the last six months. If credentials are supplied but invalid, the request is rejected as unauthorized.
 * @summary Get yield chart data for an asset
 * {@link /v1/charts/:asset_id/yield}
 */
export function useGetAssetYieldData<
  TData = GetAssetYieldDataStatus200,
  TQueryData = GetAssetYieldDataStatus200,
  TQueryKey extends QueryKey = GetAssetYieldDataQueryKey,
>(
  {
    path,
    query,
  }: {
    path: GetAssetYieldDataOptions['path'] | (() => GetAssetYieldDataOptions['path'])
    query: GetAssetYieldDataOptions['query'] | (() => GetAssetYieldDataOptions['query'])
  },
  options: {
    query?: Partial<
      QueryObserverOptions<
        GetAssetYieldDataStatus200,
        ResponseErrorConfig<
          | GetAssetYieldDataStatus400
          | GetAssetYieldDataStatus404
          | GetAssetYieldDataStatus500
          | GetAssetYieldDataStatus504
        >,
        TData,
        TQueryData,
        TQueryKey
      >
    > & { client?: QueryClient }
    client?: Partial<Omit<RequestConfig, 'path' | 'query' | 'body' | 'headers' | 'url'>>
  } = {},
) {
  const { query: queryConfig = {}, client: config = {} } = options ?? {}
  const { client: queryClient, ...resolvedOptions } = queryConfig
  const resolvedParams = {
    path: typeof path === 'function' ? path() : path,
    query: typeof query === 'function' ? query() : query,
  }
  const queryKey = resolvedOptions?.queryKey ?? getAssetYieldDataQueryKey(resolvedParams)

  const queryResult = useQuery(
    {
      ...getAssetYieldDataQueryOptions(resolvedParams, config),
      ...resolvedOptions,
      queryKey,
    } as unknown as QueryObserverOptions,
    queryClient,
  ) as UseQueryResult<
    TData,
    ResponseErrorConfig<
      | GetAssetYieldDataStatus400
      | GetAssetYieldDataStatus404
      | GetAssetYieldDataStatus500
      | GetAssetYieldDataStatus504
    >
  > & { queryKey: TQueryKey }

  queryResult.queryKey = queryKey as TQueryKey

  return queryResult
}
